import {
  type Agent,
  ChatStatus,
  type IconColorKey,
  isChatStatusBusy,
  isIconColorKey,
} from '@littlebird/sdk';

import { type BirdSpecies } from './agents-bird.species';

export type BirdLook = { species: BirdSpecies; plumage: IconColorKey };

export type BirdActivity =
  | 'idle'
  | 'thinking'
  | 'working'
  | 'waiting'
  | 'error';

export const birdActivityFor = (
  status: ChatStatus | undefined,
  { isStarting = false, hasReply = false, usingTool = false } = {}
): BirdActivity => {
  if (status === ChatStatus.Errored) return 'error';
  if (isChatStatusBusy(status) || isStarting) {
    return usingTool ? 'working' : 'thinking';
  }
  return hasReply ? 'waiting' : 'idle';
};

export const BIRD_INK: Record<IconColorKey, { light: string; dark: string }> = {
  gray: { light: 'hsl(220 10% 30%)', dark: 'hsl(220 10% 80%)' },
  violet: { light: 'hsl(262 60% 48%)', dark: 'hsl(262 80% 76%)' },
  blue: { light: 'hsl(208 78% 44%)', dark: 'hsl(206 90% 70%)' },
  green: { light: 'hsl(152 62% 32%)', dark: 'hsl(150 55% 62%)' },
  yellow: { light: 'hsl(42 90% 40%)', dark: 'hsl(46 92% 62%)' },
  amber: { light: 'hsl(22 82% 48%)', dark: 'hsl(26 90% 66%)' },
  red: { light: 'hsl(4 72% 48%)', dark: 'hsl(6 85% 70%)' },
  crimson: { light: 'hsl(338 68% 46%)', dark: 'hsl(338 80% 74%)' },
};

const FALLBACK_COLORS: IconColorKey[] = [
  'red',
  'amber',
  'crimson',
  'blue',
  'green',
  'violet',
];

export const birdLookFromName = (name: string): BirdLook => {
  let hash = 0;
  for (const char of name) {
    hash = (hash + char.charCodeAt(0)) % FALLBACK_COLORS.length;
  }
  return { species: 'hummingbird', plumage: FALLBACK_COLORS[hash] };
};

export const birdLookForAgent = (
  agent: Pick<Agent, 'icon' | 'name'>
): BirdLook => {
  const color = agent.icon?.color;
  const fallback = birdLookFromName(agent.name);
  return isIconColorKey(color) ? { ...fallback, plumage: color } : fallback;
};
